import { Component, esc } from '../core/component.js';
import { api, ApiError } from '../core/api.js';
import { icon } from '../core/icons.js';
import { t } from '../core/i18n.js';
import { stepper } from '../components/Stepper.js';
import { countryFieldHtml, wireAddressFields, addressPasteFieldHtml } from '../components/PropertyBasicsFields.js';
import { formatMoney } from '../core/format.js';

// "Beköltöztetés" (move-in) wizard. Six steps, destination first:
//   0 place      — property + which unit(s) the tenant moves into
//   1 tenant     — pick an existing party (type-ahead) OR start a new one
//   2 tenant_data— identity + structured home address (new tenant only;
//                  existing tenants show a read-only recap)
//   3 occupants  — co-movers (spouse/child/flatmate…), any number
//   4 terms      — start date + per-unit rent/közös (auto-filled from the
//                  unit) + deposit
//   5 review     — confirm, then create the DRAFT tenancy
// Rent + közös költség default from the unit's stored asking_rent /
// kozos_koltseg. A new tenant is created via POST /tenant-parties first, then
// the tenancy reuses it; co-occupants are created inline by the tenancy
// endpoint. Money is entered in major units and converted to minor on submit.

const STEP_KEYS = ['place', 'tenant', 'tenant_data', 'occupants', 'terms', 'review'];
const STREET_TYPES = ['út', 'utca', 'tér', 'körút', 'köz', 'sétány', 'rakpart', 'sugárút', 'fasor', 'dűlő', 'lakótelep', 'park'];
const RELATIONSHIPS = ['spouse', 'partner', 'child', 'parent', 'sibling', 'flatmate', 'other'];
const CHARGE_TYPES = ['utilities', 'parking', 'pet', 'cleaning', 'internet', 'other'];
// Cap on visible picker rows — large portfolios refine via search instead of scrolling.
const PROP_PICK_LIMIT = 8;
const RECENT_PARTY_LIMIT = 8;

const minorToMajor = (minor) => {
  const n = Number(minor);
  return Number.isFinite(n) && n > 0 ? String(n / 100) : '';
};
const majorToMinor = (major) => {
  const n = parseFloat(String(major).replace(',', '.'));
  return Number.isFinite(n) ? Math.round(n * 100) : NaN;
};
// Totals are accumulated in minor units — the shared formatter converts
// back to major on display (the old local copy skipped the /100, showing
// 112 000 Ft as "11 200 000 HUF").
const fmtMoney = (minor, cur) => formatMoney(Number(minor) || 0, cur || 'HUF');

// VAT (ÁFA) rates in basis points, matching backend Core\Support\VatRate.
const VAT_RATES = [0, 500, 1800, 2700];
const vatLabel = (bp) => (Number(bp) === 0 ? t('move_in_wiz.vat.aam') : Number(bp) / 100 + '%');
const vatSelect = (attrs, value) =>
  `<select class="select" ${attrs}>${VAT_RATES.map((r) => `<option value="${r}" ${Number(value ?? 0) === r ? 'selected' : ''}>${esc(vatLabel(r))}</option>`).join('')}</select>`;
const addVat = (minor, bp) => minor + Math.round(minor * (Number(bp) || 0) / 10000);

export class MoveInView extends Component {
  constructor(ctx = {}) {
    super();
    const q = (ctx && ctx.query) || {};
    this._initialUnitId = String(q.unit || '');
    this._initialPartyId = String(q.party || '');
    this.state = {
      loading: true,
      error: null,
      step: 0,
      formError: null,
      busy: false,
      // place
      properties: [],
      selectedPropertyId: String(q.property || ''),
      propertyQuery: '',
      propertyPickerOpen: false,
      propertyVacantOnly: false,
      units: null,
      selectedUnitIds: [],
      // tenant
      tenantMode: 'existing',
      partyResults: [],
      partyQuery: '',
      partySearching: false,
      recentParties: null,
      selectedParty: null,
      newTenant: { country: 'HU' },
      // occupants
      occupants: [],
      occAdding: false,
      occAddMode: 'new',
      occResults: [],
      occSearching: false,
      occQuery: '',
      occSelected: null,
      // charges
      charges: [],
      // terms
      startDate: new Date().toISOString().slice(0, 10),
      endDate: '',
      termType: 'open',
      currency: 'HUF',
      rents: {},
      kozos: {},
      rentVat: {},
      kozosVat: {},
      deposit: '',
    };
  }

  // ── lifecycle ──────────────────────────────────────────────────────────
  template() {
    const back = `<a href="#/tenancies" class="back-link">${icon('arrow-left', { size: 'sm' })}<span>${esc(t('move_in.back'))}</span></a>`;
    if (this.state.loading) {
      return `<section class="stack">${back}${this.state.error
        ? `<div class="error-banner">${esc(this.state.error)}</div>`
        : `<div class="muted">${esc(t('common.loading'))}</div>`}</section>`;
    }
    const steps = STEP_KEYS.map((k) => ({ key: k, label: t('move_in_wiz.steps.' + k) }));
    const current = this.state.step + 1;
    const isLast = this.state.step === STEP_KEYS.length - 1;
    return `
      <section class="stack">
        ${back}
        <h2 style="margin:0">${esc(t('move_in.title'))}</h2>
        <div class="panel">
          <div class="panel-head">${stepper(steps, current, { onClickable: true })}</div>
          <div class="panel-body">
            <div class="wizard-head">
              <div class="wizard-head-text">
                <div class="wizard-progress-text">${current} / ${STEP_KEYS.length}</div>
                <div class="wizard-step-name">${esc(t('move_in_wiz.steps.' + STEP_KEYS[this.state.step]))}</div>
              </div>
              <div class="wizard-progress-bar"><span style="width:${Math.round((current / STEP_KEYS.length) * 100)}%"></span></div>
            </div>
            ${this.state.formError ? `<div class="error-banner" style="margin-bottom:var(--space-3)">${esc(this.state.formError)}</div>` : ''}
            <div data-step-body>${this._stepBody()}</div>
            <div class="wizard-nav">
              ${this.state.step > 0 ? `<button class="btn btn-ghost" data-nav="prev">${esc(t('move_in_wiz.nav.back'))}</button>` : ''}
              <span class="grow"></span>
              ${isLast
                ? `<button class="btn btn-primary" data-nav="create" ${this.state.busy ? 'disabled' : ''}>${esc(this.state.busy ? t('move_in_wiz.nav.creating') : t('move_in_wiz.nav.create'))} ${icon('check', { size: 'sm' })}</button>`
                : `<button class="btn btn-primary" data-nav="next">${esc(t('move_in_wiz.nav.next'))}</button>`}
            </div>
          </div>
        </div>
      </section>`;
  }

  _stepBody() {
    switch (this.state.step) {
      case 0: return this._placeHtml();
      case 1: return this._tenantHtml();
      case 2: return this._tenantDataHtml();
      case 3: return this._occupantsHtml();
      case 4: return this._termsHtml();
      case 5: return this._reviewHtml();
      default: return '';
    }
  }

  mount() {
    if (this.state.loading) {
      if (!this._loadStarted) { this._loadStarted = true; this._load(); }
      return;
    }
    const root = this.el;
    if (!root) return;
    // Stepper jump (only to completed steps).
    root.querySelectorAll('[data-step-jump]').forEach((b) => b.addEventListener('click', () => {
      const idx = Number(b.getAttribute('data-step-jump')) - 1;
      if (idx < this.state.step) { this._capture(); this.setState({ step: idx, formError: null }); }
    }));
    root.querySelectorAll('[data-nav]').forEach((b) => b.addEventListener('click', () => this._onNav(b.getAttribute('data-nav'))));
    root.querySelectorAll('[data-goto]').forEach((b) => b.addEventListener('click', () => { this._capture(); this.setState({ step: Number(b.getAttribute('data-goto')), formError: null }); }));
    this._wireStep();
  }

  // ── navigation ─────────────────────────────────────────────────────────
  _onNav(dir) {
    if (dir === 'prev') { this._capture(); this.setState({ step: this.state.step - 1, formError: null }); return; }
    this._capture();
    const err = this._validate(this.state.step);
    if (err) { this.setState({ formError: err }); return; }
    if (dir === 'create') { this._save(); return; }
    // New tenant skips nothing; existing tenant keeps the (read-only) data step.
    this.setState({ step: this.state.step + 1, formError: null });
  }

  _validate(step) {
    const st = this.state;
    if (step === 0) {
      if (!st.selectedPropertyId) return t('move_in_wiz.errors.property_required');
      if (!st.selectedUnitIds.length) return t('move_in_wiz.errors.unit_required');
    }
    if (step === 1) {
      if (st.tenantMode === 'existing' && !st.selectedParty) return t('move_in_wiz.errors.tenant_required');
    }
    if (step === 2 && st.tenantMode === 'new') {
      if (!String(st.newTenant.display_name || '').trim()) return t('move_in_wiz.errors.name_required');
    }
    if (step === 4) {
      if (!String(st.startDate || '').trim()) return t('move_in_wiz.errors.start_required');
      if (st.termType === 'fixed' && st.endDate && st.endDate < st.startDate) return t('move_in_wiz.errors.end_before_start');
      for (const id of st.selectedUnitIds) {
        for (const map of [st.rents, st.kozos]) {
          const v = map[id];
          if (v !== undefined && v !== '' && !(Number.isFinite(parseFloat(v)) && parseFloat(v) >= 0)) return t('move_in_wiz.errors.rent_invalid');
        }
      }
      const dep = st.deposit;
      if (dep !== '' && !(Number.isFinite(parseFloat(dep)) && parseFloat(dep) >= 0)) return t('move_in_wiz.errors.rent_invalid');
    }
    return null;
  }

  // Read the current step's inputs into state so a re-render never loses them.
  _capture() {
    const root = this.el; if (!root) return;
    const v = (sel) => { const e = root.querySelector(sel); return e ? e.value : undefined; };
    const st = this.state;
    if (st.step === 2 && st.tenantMode === 'new') {
      const nt = { ...st.newTenant };
      root.querySelectorAll('[data-nt]').forEach((e) => { nt[e.getAttribute('data-nt')] = e.value; });
      st.newTenant = nt;
    }
    if (st.step === 4) {
      const sd = v('[name="start_date"]'); if (sd !== undefined) st.startDate = sd;
      const ed = v('[name="end_date"]'); if (ed !== undefined) st.endDate = ed;
      const cur = v('[name="currency"]'); if (cur !== undefined) st.currency = cur;
      const dep = v('[name="deposit"]'); if (dep !== undefined) st.deposit = dep;
      const rents = { ...st.rents }, kozos = { ...st.kozos };
      const rentVat = { ...st.rentVat }, kozosVat = { ...st.kozosVat };
      root.querySelectorAll('[data-rent]').forEach((e) => { rents[e.getAttribute('data-rent')] = e.value; });
      root.querySelectorAll('[data-kozos]').forEach((e) => { kozos[e.getAttribute('data-kozos')] = e.value; });
      root.querySelectorAll('[data-rent-vat]').forEach((e) => { rentVat[e.getAttribute('data-rent-vat')] = e.value; });
      root.querySelectorAll('[data-kozos-vat]').forEach((e) => { kozosVat[e.getAttribute('data-kozos-vat')] = e.value; });
      st.rents = rents; st.kozos = kozos; st.rentVat = rentVat; st.kozosVat = kozosVat;
      this._captureCharges();
    }
  }

  _captureCharges() {
    const root = this.el; if (!root) return;
    root.querySelectorAll('[data-charge-row]').forEach((row) => {
      const i = Number(row.getAttribute('data-charge-row'));
      const c = this.state.charges[i]; if (!c) return;
      const type = row.querySelector('[data-charge-type]');
      const amt = row.querySelector('[data-charge-amount]');
      const freq = row.querySelector('[data-charge-freq]');
      const vat = row.querySelector('[data-charge-vat]');
      if (type) c.charge_type = type.value;
      if (amt) c.amount = amt.value;
      if (freq) c.frequency = freq.value;
      if (vat) c.vat_rate = vat.value;
    });
  }

  _wireStep() {
    const m = { 0: '_wirePlace', 1: '_wireTenant', 2: '_wireData', 3: '_wireOccupants', 4: '_wireTerms' }[this.state.step];
    if (m && this[m]) this[m]();
  }

  // ── step 0: place ────────────────────────────────────────────────────
  _placeHtml() {
    const props = this.state.properties || [];
    const selected = props.find((p) => p.id === this.state.selectedPropertyId) || null;
    // Collapsed once chosen: a summary row + "change" instead of the full
    // picker, so a 100-property portfolio never buries the unit list.
    if (selected && !this.state.propertyPickerOpen) {
      const address = [selected.postal_code, selected.city, selected.address_line1].filter(Boolean).join(' ');
      return `
        <div class="stack">
          <div class="field">
            <label>${esc(t('move_in_wiz.place.property'))}</label>
            <div class="picked-row">
              <span class="stack-2 grow">
                <strong>${esc(selected.name)}</strong>
                ${address ? `<span class="faint">${esc(address)}</span>` : ''}
              </span>
              <button type="button" class="btn btn-ghost btn-sm" data-role="property-change">${esc(t('move_in_wiz.place.change'))}</button>
            </div>
          </div>
          ${this._unitsHtml()}
        </div>`;
    }
    // Type-ahead + vacancy filter only once the portfolio outgrows a glanceable grid.
    const tools = props.length > 5 ? `
        <div class="row" style="gap:var(--space-2); flex-wrap:wrap">
          <div class="search-box grow">
            ${icon('search', { size: 'sm', class: 'search-box-icon' })}
            <input class="input" data-role="property-search" placeholder="${esc(t('move_in_wiz.place.search_placeholder'))}" value="${esc(this.state.propertyQuery || '')}" />
          </div>
          <button type="button" class="seg-btn${this.state.propertyVacantOnly ? ' active' : ''}" data-role="vacant-toggle">${esc(t('move_in_wiz.place.vacant_only'))}</button>
        </div>` : '';
    return `
      <div class="stack">
        <p class="muted" style="margin:0">${esc(t('move_in_wiz.place.hint'))}</p>
        <div class="field">
          <label>${esc(t('move_in_wiz.place.property'))}</label>
          ${tools}
          <div class="prop-pick-grid" data-role="property-grid">${this._propertyCardsHtml()}</div>
        </div>
        ${this._unitsHtml()}
      </div>`;
  }

  _filteredProperties() {
    const props = (this.state.properties || []).slice();
    const q = (this.state.propertyQuery || '').trim().toLowerCase();
    let out = q
      ? props.filter((p) => [p.name, p.city, p.address_line1, p.postal_code].filter(Boolean).join(' ').toLowerCase().includes(q))
      : props;
    if (this.state.propertyVacantOnly) out = out.filter((p) => Number(p.vacant_unit_count ?? 0) > 0);
    // Pickable options first: vacancies float to the top, then by name.
    return out.sort((a, b) => {
      const av = Number(a.vacant_unit_count ?? 0) > 0 ? 0 : 1;
      const bv = Number(b.vacant_unit_count ?? 0) > 0 ? 0 : 1;
      return av - bv || String(a.name || '').localeCompare(String(b.name || ''), 'hu');
    });
  }

  _propertyCardsHtml() {
    const props = this._filteredProperties();
    if (!props.length) return `<div class="faint">${esc(t('move_in_wiz.place.no_matches'))}</div>`;
    const shown = props.slice(0, PROP_PICK_LIMIT);
    const rest = props.length - shown.length;
    return shown.map((p) => this._propertyCard(p)).join('')
      + (rest > 0 ? `<div class="faint prop-pick-more">${esc(t('move_in_wiz.place.more_matches', { count: rest }))}</div>` : '');
  }

  _propertyCard(p) {
    const selected = this.state.selectedPropertyId === p.id;
    const unitCount = Number(p.unit_count ?? 0);
    const vacant = Number(p.vacant_unit_count ?? 0);
    const noUnits = unitCount === 0;
    const address = [p.postal_code, p.city, p.address_line1].filter(Boolean).join(' ');
    const occBadge = noUnits
      ? `<span class="badge badge-muted">${esc(t('move_in_wiz.place.no_units_badge'))}</span>`
      : vacant > 0
        ? `<span class="badge badge-success">${esc(t('move_in_wiz.place.vacant_count', { count: vacant }))}</span>`
        : `<span class="badge badge-warning">${esc(t('move_in_wiz.place.all_occupied'))}</span>`;
    return `
      <button type="button" class="prop-pick-card${selected ? ' is-selected' : ''}" data-prop-pick="${esc(p.id)}" ${noUnits ? 'disabled' : ''}>
        <span class="prop-pick-check">${selected ? icon('check', { size: 'sm' }) : ''}</span>
        <span class="stack-2 grow" style="text-align:left">
          <strong>${esc(p.name)}</strong>
          ${address ? `<span class="faint">${esc(address)}</span>` : ''}
          <span class="row" style="gap:var(--space-2); flex-wrap:wrap">
            <span class="badge">${esc(t(unitCount === 1 ? 'move_in_wiz.place.units_count_one' : 'move_in_wiz.place.units_count_other', { count: unitCount }))}</span>
            ${occBadge}
          </span>
        </span>
      </button>`;
  }

  _unitsHtml() {
    if (!this.state.selectedPropertyId) return '';
    if (this.state.units === null) return `<div class="muted">${esc(t('move_in_wiz.place.loading_units'))}</div>`;
    if (this.state.units.length === 0) return `<div class="warn-banner">${esc(t('move_in_wiz.place.no_units'))}</div>`;
    return `
      <div class="stack-2">
        <span class="faint">${esc(t('move_in_wiz.place.units_all'))}</span>
        ${this.state.units.map((u) => this._unitCard(u)).join('')}
      </div>`;
  }

  _unitCard(u) {
    const selected = this.state.selectedUnitIds.includes(u.id);
    const blocked = u.availability_status === 'occupied' && u.current_tenancy_open_ended;
    const badge = u.availability_status === 'vacant'
      ? `<span class="badge badge-success">${esc(t('new_tenancy.vacant'))}</span>`
      : u.current_tenancy_open_ended
        ? `<span class="badge badge-muted">${esc(t('move_in_wiz.place.occupied'))}</span>`
        : `<span class="badge badge-warning">${esc(t('new_tenancy.free_from', { date: u.earliest_legal_start_date || '' }))}</span>`;
    const size = u.size_sqm ? ` · ${esc(u.size_sqm)} m²` : '';
    return `
      <button type="button" class="unit-select-card${selected ? ' is-selected' : ''}" data-unit-pick="${esc(u.id)}" ${blocked ? 'disabled' : ''}>
        <span class="unit-select-check">${selected ? icon('check', { size: 'sm' }) : ''}</span>
        <span class="stack-2 grow" style="text-align:left">
          <strong>${esc(u.identifier)}</strong>
          <span class="faint">${esc(u.type || '')}${size}</span>
        </span>
        ${badge}
      </button>`;
  }

  _wirePlace() {
    const root = this.el;
    const change = root.querySelector('[data-role="property-change"]');
    if (change) change.addEventListener('click', () => this.setState({ propertyPickerOpen: true }));
    // Targeted DOM update keeps the search input focused (no full re-render).
    const refreshGrid = () => {
      const grid = root.querySelector('[data-role="property-grid"]');
      if (grid) {
        grid.innerHTML = this._propertyCardsHtml();
        this._wirePropertyPicks(grid);
      }
    };
    const search = root.querySelector('[data-role="property-search"]');
    if (search) {
      search.addEventListener('input', (e) => {
        this.state.propertyQuery = e.target.value;
        refreshGrid();
      });
    }
    const vacant = root.querySelector('[data-role="vacant-toggle"]');
    if (vacant) {
      vacant.addEventListener('click', () => {
        this.state.propertyVacantOnly = !this.state.propertyVacantOnly;
        vacant.classList.toggle('active', this.state.propertyVacantOnly);
        refreshGrid();
      });
    }
    this._wirePropertyPicks(root);
    root.querySelectorAll('[data-unit-pick]').forEach((b) => b.addEventListener('click', () => {
      const id = b.getAttribute('data-unit-pick');
      const set = new Set(this.state.selectedUnitIds);
      set.has(id) ? set.delete(id) : set.add(id);
      this.setState({ selectedUnitIds: [...set] });
    }));
  }

  _wirePropertyPicks(scope) {
    scope.querySelectorAll('[data-prop-pick]').forEach((b) => b.addEventListener('click', () => {
      const id = b.getAttribute('data-prop-pick');
      if (id === this.state.selectedPropertyId) { this.setState({ propertyPickerOpen: false }); return; }
      this.setState({ selectedPropertyId: id, propertyPickerOpen: false, units: null, selectedUnitIds: [], formError: null });
      this._loadUnits(id);
    }));
  }

  // ── step 1: tenant ───────────────────────────────────────────────────
  _tenantHtml() {
    const seg = (mode, label) => `<button type="button" class="seg-btn${this.state.tenantMode === mode ? ' active' : ''}" data-tmode="${mode}">${esc(label)}</button>`;
    const body = this.state.tenantMode === 'existing' ? this._existingTenantHtml() : `<p class="muted" style="margin:0">${esc(t('move_in_wiz.tenant.new_hint'))}</p>`;
    return `
      <div class="stack">
        <div class="seg" style="align-self:flex-start">
          ${seg('existing', t('move_in_wiz.tenant.mode_existing'))}
          ${seg('new', t('move_in_wiz.tenant.mode_new'))}
        </div>
        ${body}
      </div>`;
  }

  _existingTenantHtml() {
    const sp = this.state.selectedParty;
    // Collapsed once chosen: search + results give way to a summary row.
    if (sp) {
      return `
        <div class="picked-row">
          <span class="stack-2 grow"><strong>${esc(sp.display_name)}</strong><span class="faint">${esc(sp.email || '')}</span></span>
          <span class="badge badge-success">${icon('check', { size: 'sm' })} ${esc(t('move_in_wiz.tenant.selected'))}</span>
          <button type="button" class="btn btn-ghost btn-sm" data-role="party-change">${esc(t('move_in_wiz.tenant.change'))}</button>
        </div>`;
    }
    return `
      <div class="stack-2">
        <div class="search-box">
          ${icon('search', { size: 'sm', class: 'search-box-icon' })}
          <input class="input" data-role="party-search" placeholder="${esc(t('move_in_wiz.tenant.search_placeholder'))}" value="${esc(this.state.partyQuery)}" />
        </div>
        <div data-results>${this._partyResultsHtml()}</div>
        <button type="button" class="btn btn-ghost btn-sm" data-tmode="new" style="align-self:flex-start">${icon('user-plus', { size: 'sm' })}<span>${esc(t('move_in_wiz.tenant.create_hint'))}</span></button>
      </div>`;
  }

  _partyResultsHtml() {
    if (this.state.partySearching) return `<div class="muted">${esc(t('move_in_wiz.tenant.searching'))}</div>`;
    const q = (this.state.partyQuery || '').trim();
    if (q.length < 2) {
      // No query yet → most-recently-touched parties, so picking needs no typing.
      const recent = this.state.recentParties;
      if (recent === null) return `<div class="muted">${esc(t('common.loading'))}</div>`;
      if (!recent.length) return `<div class="faint">${esc(t('move_in_wiz.tenant.none_yet'))}</div>`;
      return `<div class="stack-2"><span class="faint">${esc(t('move_in_wiz.tenant.recent'))}</span>${this._partyListHtml(recent)}</div>`;
    }
    const res = this.state.partyResults || [];
    if (!res.length) return `<div class="faint">${esc(t('move_in_wiz.tenant.no_match'))}</div>`;
    return this._partyListHtml(res);
  }

  _partyListHtml(list) {
    return `<div class="list">${list.map((p) => `
      <button type="button" class="list-item" data-pick-party="${esc(p.id)}" style="width:100%;text-align:left;background:none;border:1px solid var(--color-border);cursor:pointer">
        <div class="stack-2 grow"><strong>${esc(p.display_name)}</strong><span class="faint">${esc(p.email || '')}${p.birth_date ? ' · ' + esc(p.birth_date) : ''}</span></div>
        ${p.active_property_names ? `<span class="badge badge-muted">${esc(t('move_in_wiz.tenant.lives_at', { name: p.active_property_names }))}</span>` : ''}
      </button>`).join('')}</div>`;
  }

  _wirePartyPicks(scope) {
    scope.querySelectorAll('[data-pick-party]').forEach((b) => b.addEventListener('click', () => {
      const id = b.getAttribute('data-pick-party');
      const pool = [...(this.state.partyResults || []), ...(this.state.recentParties || [])];
      this.setState({ selectedParty: pool.find((p) => p.id === id) || null, formError: null });
    }));
  }

  _wireTenant() {
    const root = this.el;
    root.querySelectorAll('[data-tmode]').forEach((b) => b.addEventListener('click', () => {
      this.setState({ tenantMode: b.getAttribute('data-tmode'), formError: null });
    }));
    const change = root.querySelector('[data-role="party-change"]');
    if (change) change.addEventListener('click', () => this.setState({ selectedParty: null }));
    const search = root.querySelector('[data-role="party-search"]');
    if (search) {
      search.addEventListener('input', (e) => {
        this.state.partyQuery = e.target.value;
        clearTimeout(this._searchTimer);
        this._searchTimer = setTimeout(() => this._searchParties(e.target.value), 250);
      });
    }
    this._wirePartyPicks(root);
    if (this.state.tenantMode === 'existing' && !this.state.selectedParty && this.state.recentParties === null) {
      this._loadRecentParties();
    }
  }

  async _loadRecentParties() {
    try {
      const res = await api.get('/tenant-parties');
      this.state.recentParties = (res.data || []).slice(0, RECENT_PARTY_LIMIT);
    } catch { this.state.recentParties = []; }
    const el = this.el && this.el.querySelector('[data-results]');
    if (el) { el.innerHTML = this._partyResultsHtml(); this._wirePartyPicks(el); }
  }

  async _searchParties(q) {
    const query = (q || '').trim();
    const resultsEl = this.el && this.el.querySelector('[data-results]');
    if (query.length < 2) {
      if (resultsEl) { resultsEl.innerHTML = this._partyResultsHtml(); this._wirePartyPicks(resultsEl); }
      return;
    }
    this.state.partySearching = true;
    if (resultsEl) resultsEl.innerHTML = `<div class="muted">${esc(t('move_in_wiz.tenant.searching'))}</div>`;
    try {
      const res = await api.get(`/tenant-parties?q=${encodeURIComponent(query)}`);
      this.state.partyResults = res.data || [];
    } catch { this.state.partyResults = []; }
    this.state.partySearching = false;
    // Targeted DOM update keeps the search input focused (no full re-render).
    const el = this.el && this.el.querySelector('[data-results]');
    if (el) { el.innerHTML = this._partyResultsHtml(); this._wirePartyPicks(el); }
  }

  // ── step 2: tenant data ────────────────────────────────────────────────
  _tenantDataHtml() {
    if (this.state.tenantMode === 'existing') {
      const p = this.state.selectedParty || {};
      const row = (k, val) => `<div class="kv-row"><span class="faint">${esc(t('move_in_wiz.data.' + k))}</span><strong>${esc(val || '—')}</strong></div>`;
      return `
        <div class="stack">
          <p class="muted" style="margin:0">${esc(t('move_in_wiz.data.existing_readonly'))}</p>
          <div class="card stack-2">
            ${row('name', p.display_name)}
            ${row('birth_name', p.birth_name)}
            ${row('mother_name', p.mother_name)}
            ${row('birth_place', p.birth_place)}
            ${row('birth_date', p.birth_date)}
            ${row('email', p.email)}
            ${row('phone', p.phone)}
            ${row('address', [p.postal_code, p.city, p.address_line1].filter(Boolean).join(' '))}
          </div>
        </div>`;
    }
    const nt = this.state.newTenant;
    const f = (k, attrs = '') => `<input class="input" data-nt="${k}" name="${k}" value="${esc(nt[k] || '')}" ${attrs} />`;
    return `
      <form class="stack" data-data-form>
        <div class="sub-head">${esc(t('move_in_wiz.data.identity'))}</div>
        <div class="field"><label>${esc(t('move_in_wiz.data.name'))} <span class="req">*</span></label>${f('display_name', 'maxlength="190"')}</div>
        <div class="field-row" style="grid-template-columns:1fr 1fr">
          <div class="field"><label>${esc(t('move_in_wiz.data.birth_name'))}</label>${f('birth_name', 'maxlength="190"')}</div>
          <div class="field"><label>${esc(t('move_in_wiz.data.mother_name'))}</label>${f('mother_name', 'maxlength="190"')}</div>
        </div>
        <div class="field-row" style="grid-template-columns:1fr 1fr">
          <div class="field"><label>${esc(t('move_in_wiz.data.birth_place'))}</label>${f('birth_place', 'maxlength="190"')}</div>
          <div class="field"><label>${esc(t('move_in_wiz.data.birth_date'))}</label>${f('birth_date', 'type="date"')}</div>
        </div>
        <div class="field-row" style="grid-template-columns:1fr 1fr">
          <div class="field"><label>${esc(t('move_in_wiz.data.email'))} <span class="faint">(${esc(t('common.optional'))})</span></label>${f('email', 'type="email" maxlength="190"')}</div>
          <div class="field"><label>${esc(t('move_in_wiz.data.phone'))} <span class="faint">(${esc(t('common.optional'))})</span></label>${f('phone', 'maxlength="64"')}</div>
        </div>
        <div data-dup-hint></div>

        <div class="sub-head">${esc(t('move_in_wiz.data.address'))}</div>
        ${addressPasteFieldHtml()}
        <div class="field"><label>${esc(t('move_in_wiz.data.country'))}</label>${countryFieldHtml(nt.country || 'HU', { name: 'country' })}</div>
        <div class="field-row" style="grid-template-columns:140px 1fr">
          <div class="field"><label>${esc(t('move_in_wiz.data.postal_code'))}</label><input class="input" data-nt="postal_code" name="postal_code" maxlength="16" value="${esc(nt.postal_code || '')}" /></div>
          <div class="field"><label>${esc(t('move_in_wiz.data.city'))}</label><input class="input" data-nt="city" name="city" maxlength="128" value="${esc(nt.city || '')}" /></div>
        </div>
        <div class="field-row" style="grid-template-columns:1fr 150px">
          <div class="field"><label>${esc(t('move_in_wiz.data.street'))}</label>${f('addr_street', 'maxlength="190"')}</div>
          <div class="field"><label>${esc(t('move_in_wiz.data.street_type'))}</label>
            <select class="select" data-nt="addr_street_type" name="addr_street_type">${STREET_TYPES.map((s) => `<option ${nt.addr_street_type === s ? 'selected' : ''}>${esc(s)}</option>`).join('')}</select>
          </div>
        </div>
        <div class="field-row" style="grid-template-columns:1fr 1fr 1fr">
          <div class="field"><label>${esc(t('move_in_wiz.data.house_number'))}</label>${f('addr_house_number', 'maxlength="32"')}</div>
          <div class="field"><label>${esc(t('move_in_wiz.data.building'))} <span class="faint">(${esc(t('common.optional'))})</span></label>${f('addr_building', 'maxlength="32"')}</div>
          <div class="field"><label>${esc(t('move_in_wiz.data.staircase'))} <span class="faint">(${esc(t('common.optional'))})</span></label>${f('addr_staircase', 'maxlength="32"')}</div>
        </div>
        <div class="field-row" style="grid-template-columns:1fr 1fr">
          <div class="field"><label>${esc(t('move_in_wiz.data.floor'))} <span class="faint">(${esc(t('common.optional'))})</span></label>${f('addr_floor', 'maxlength="32"')}</div>
          <div class="field"><label>${esc(t('move_in_wiz.data.door'))} <span class="faint">(${esc(t('common.optional'))})</span></label>${f('addr_door', 'maxlength="32"')}</div>
        </div>
      </form>`;
  }

  _wireData() {
    if (this.state.tenantMode !== 'new') return;
    const form = this.el.querySelector('[data-data-form]');
    if (form) wireAddressFields(form, { requireDefault: true });
    // Duplicate guard: an existing party matching the typed name/email is
    // offered for reuse instead of creating a twin record.
    ['display_name', 'email'].forEach((k) => {
      const input = this.el.querySelector(`[data-nt="${k}"]`);
      if (input) input.addEventListener('input', () => {
        clearTimeout(this._dupTimer);
        this._dupTimer = setTimeout(() => this._checkDuplicates(), 350);
      });
    });
  }

  async _checkDuplicates() {
    const root = this.el; if (!root) return;
    const hint = root.querySelector('[data-dup-hint]'); if (!hint) return;
    const val = (k) => { const e = root.querySelector(`[data-nt="${k}"]`); return e ? e.value.trim() : ''; };
    const email = val('email');
    const q = email.length >= 3 ? email : val('display_name');
    if (q.length < 3) { hint.innerHTML = ''; return; }
    let matches = [];
    try { matches = ((await api.get(`/tenant-parties?q=${encodeURIComponent(q)}`)).data || []).slice(0, 3); } catch { matches = []; }
    if (!matches.length) { hint.innerHTML = ''; return; }
    this._dupMatches = matches;
    hint.innerHTML = `
      <div class="warn-banner stack-2">
        <strong>${esc(t('move_in_wiz.data.dup_title'))}</strong>
        ${matches.map((p) => `
          <div class="row-between">
            <span>${esc(p.display_name)}${p.email ? ' · ' + esc(p.email) : ''}</span>
            <button type="button" class="btn btn-ghost btn-sm" data-dup-use="${esc(p.id)}">${esc(t('move_in_wiz.data.dup_use'))}</button>
          </div>`).join('')}
      </div>`;
    hint.querySelectorAll('[data-dup-use]').forEach((b) => b.addEventListener('click', () => {
      const party = (this._dupMatches || []).find((p) => p.id === b.getAttribute('data-dup-use'));
      if (party) this.setState({ tenantMode: 'existing', selectedParty: party, formError: null });
    }));
  }

  // ── step 3: occupants ────────────────────────────────────────────────
  _occupantsHtml() {
    const p = this.state.tenantMode === 'existing' ? (this.state.selectedParty || {}) : this.state.newTenant;
    const primaryName = p.display_name || t('move_in_wiz.review.tenant');
    const list = this.state.occupants.map((o, i) => `
      <div class="list-item">
        <div class="stack-2 grow"><strong>${esc(o.display_name || '—')}</strong><span class="faint">${esc(t('move_in_wiz.rel.' + o.relationship))}</span></div>
        <span class="badge badge-accent">${esc(t('move_in_wiz.rel.' + o.relationship))}</span>
        <button type="button" class="icon-btn" data-occ-rm="${i}" title="${esc(t('move_in_wiz.occ.remove'))}">${icon('trash', { size: 'sm' })}</button>
      </div>`).join('');
    return `
      <div class="stack">
        <p class="muted" style="margin:0">${esc(t('move_in_wiz.occ.hint'))}</p>
        <div class="list-item" style="border-color:var(--color-primary)">
          <div class="stack-2 grow"><strong>${esc(primaryName)}</strong><span class="faint">${esc(t('move_in_wiz.occ.primary'))}</span></div>
          <span class="badge badge-success">${esc(t('move_in_wiz.occ.primary'))}</span>
        </div>
        ${list || `<div class="faint">${esc(t('move_in_wiz.occ.none'))}</div>`}
        ${this.state.occAdding ? this._occAddPanel() : `<button type="button" class="btn btn-ghost" data-occ-add style="align-self:flex-start">${esc(t('move_in_wiz.occ.add'))}</button>`}
      </div>`;
  }

  _occAddPanel() {
    const f = (k, attrs = '') => `<input class="input" data-occf="${k}" ${attrs} />`;
    const seg = (mode, label) => `<button type="button" class="seg-btn${this.state.occAddMode === mode ? ' active' : ''}" data-occ-mode="${mode}">${esc(label)}</button>`;
    const relSelect = `<select class="select" data-occf="relationship">${RELATIONSHIPS.map((r) => `<option value="${r}">${esc(t('move_in_wiz.rel.' + r))}</option>`).join('')}</select>`;
    const body = this.state.occAddMode === 'existing'
      ? `
        <div class="field"><label>${esc(t('move_in_wiz.occ.relationship'))}</label>${relSelect}</div>
        <div class="search-box">
          ${icon('search', { size: 'sm', class: 'search-box-icon' })}
          <input class="input" data-occ-search placeholder="${esc(t('move_in_wiz.occ.search_placeholder'))}" value="${esc(this.state.occQuery)}" />
        </div>
        <div data-occ-results>${this._occResultsHtml()}</div>`
      : `
        <div class="field-row" style="grid-template-columns:1fr 1fr">
          <div class="field"><label>${esc(t('move_in_wiz.data.name'))} <span class="req">*</span></label>${f('display_name', 'maxlength="190"')}</div>
          <div class="field"><label>${esc(t('move_in_wiz.occ.relationship'))}</label>${relSelect}</div>
        </div>
        <div class="field-row" style="grid-template-columns:1fr 1fr">
          <div class="field"><label>${esc(t('move_in_wiz.data.birth_place'))}</label>${f('birth_place', 'maxlength="190"')}</div>
          <div class="field"><label>${esc(t('move_in_wiz.data.birth_date'))}</label>${f('birth_date', 'type="date"')}</div>
        </div>
        <div class="field-row" style="grid-template-columns:1fr 1fr">
          <div class="field"><label>${esc(t('move_in_wiz.data.birth_name'))}</label>${f('birth_name', 'maxlength="190"')}</div>
          <div class="field"><label>${esc(t('move_in_wiz.data.mother_name'))}</label>${f('mother_name', 'maxlength="190"')}</div>
        </div>
        <div class="row" style="justify-content:flex-end;gap:var(--space-2)">
          <button type="button" class="btn btn-ghost btn-sm" data-occ-cancel>${esc(t('common.cancel'))}</button>
          <button type="button" class="btn btn-primary btn-sm" data-occ-save>${esc(t('move_in_wiz.occ.save'))}</button>
        </div>`;
    return `
      <div class="card stack" data-occ-panel>
        <div class="seg" style="align-self:flex-start">
          ${seg('new', t('move_in_wiz.occ.mode_new'))}
          ${seg('existing', t('move_in_wiz.occ.mode_existing'))}
        </div>
        ${body}
        ${this.state.occAddMode === 'existing' ? `<div class="row" style="justify-content:flex-end"><button type="button" class="btn btn-ghost btn-sm" data-occ-cancel>${esc(t('common.cancel'))}</button></div>` : ''}
      </div>`;
  }

  _occResultsHtml() {
    if (this.state.occSearching) return `<div class="muted">${esc(t('move_in_wiz.tenant.searching'))}</div>`;
    if ((this.state.occQuery || '').trim().length < 2) return `<div class="faint">${esc(t('move_in_wiz.tenant.type_more'))}</div>`;
    const res = this.state.occResults || [];
    if (!res.length) return `<div class="faint">${esc(t('move_in_wiz.tenant.no_match'))}</div>`;
    return `<div class="list">${res.map((p) => `
      <button type="button" class="list-item" data-occ-pick="${esc(p.id)}" style="width:100%;text-align:left;background:none;border:1px solid var(--color-border);cursor:pointer">
        <div class="stack-2 grow"><strong>${esc(p.display_name)}</strong><span class="faint">${esc(p.email || '')}</span></div>
        <span class="badge badge-muted">${esc(t('move_in_wiz.occ.pick'))}</span>
      </button>`).join('')}</div>`;
  }

  _wireOccupants() {
    const root = this.el;
    const addBtn = root.querySelector('[data-occ-add]');
    if (addBtn) addBtn.addEventListener('click', () => this.setState({ occAdding: true }));
    root.querySelectorAll('[data-occ-cancel]').forEach((b) => b.addEventListener('click', () => this.setState({ occAdding: false, occQuery: '', occResults: [], occSelected: null })));
    root.querySelectorAll('[data-occ-mode]').forEach((b) => b.addEventListener('click', () => this.setState({ occAddMode: b.getAttribute('data-occ-mode') })));
    root.querySelectorAll('[data-occ-rm]').forEach((b) => b.addEventListener('click', () => {
      const i = Number(b.getAttribute('data-occ-rm'));
      const occ = this.state.occupants.slice(); occ.splice(i, 1);
      this.setState({ occupants: occ });
    }));
    const save = root.querySelector('[data-occ-save]');
    if (save) save.addEventListener('click', () => {
      const panel = root.querySelector('[data-occ-panel]');
      const o = { mode: 'new', relationship: 'other' };
      panel.querySelectorAll('[data-occf]').forEach((e) => { o[e.getAttribute('data-occf')] = e.value; });
      if (!String(o.display_name || '').trim()) { this.setState({ formError: t('move_in_wiz.errors.occ_name_required') }); return; }
      this.setState({ occupants: [...this.state.occupants, o], occAdding: false, formError: null });
    });
    // Existing-contact search + pick
    const search = root.querySelector('[data-occ-search]');
    if (search) search.addEventListener('input', (e) => {
      this.state.occQuery = e.target.value;
      clearTimeout(this._occTimer);
      this._occTimer = setTimeout(() => this._searchOccupants(e.target.value), 250);
    });
    this._wireOccPick();
  }

  _wireOccPick() {
    const root = this.el; if (!root) return;
    root.querySelectorAll('[data-occ-pick]').forEach((b) => b.addEventListener('click', () => {
      const id = b.getAttribute('data-occ-pick');
      const party = (this.state.occResults || []).find((p) => p.id === id);
      if (!party) return;
      const relEl = root.querySelector('[data-occf="relationship"]');
      const relationship = relEl ? relEl.value : 'other';
      this.setState({
        occupants: [...this.state.occupants, { mode: 'existing', tenant_party_id: party.id, display_name: party.display_name, relationship }],
        occAdding: false, occQuery: '', occResults: [], occSelected: null,
      });
    }));
  }

  async _searchOccupants(q) {
    const query = (q || '').trim();
    const el = this.el && this.el.querySelector('[data-occ-results]');
    if (query.length < 2) { if (el) el.innerHTML = `<div class="faint">${esc(t('move_in_wiz.tenant.type_more'))}</div>`; return; }
    this.state.occSearching = true;
    if (el) el.innerHTML = `<div class="muted">${esc(t('move_in_wiz.tenant.searching'))}</div>`;
    try {
      const res = await api.get(`/tenant-parties?q=${encodeURIComponent(query)}`);
      // Exclude the primary tenant + already-added occupants.
      const taken = new Set([this.state.selectedParty && this.state.selectedParty.id, ...this.state.occupants.map((o) => o.tenant_party_id)].filter(Boolean));
      this.state.occResults = (res.data || []).filter((p) => !taken.has(p.id));
    } catch { this.state.occResults = []; }
    this.state.occSearching = false;
    const el2 = this.el && this.el.querySelector('[data-occ-results]');
    if (el2) { el2.innerHTML = this._occResultsHtml(); this._wireOccPick(); }
  }

  // ── step 4: terms ──────────────────────────────────────────────────────
  _termsHtml() {
    const st = this.state;
    const seg = (val, label) => `<button type="button" class="seg-btn${st.termType === val ? ' active' : ''}" data-term="${val}">${esc(label)}</button>`;
    const rentRows = st.selectedUnitIds.map((id) => {
      const u = (st.units || []).find((x) => x.id === id) || {};
      return `
        <div class="card stack-2">
          <strong>${esc(u.identifier || id)}</strong>
          <div class="field-row" style="grid-template-columns:1fr 1fr">
            <div class="field"><label>${esc(t('move_in_wiz.terms.rent'))}</label><input class="input" type="number" min="0" step="1" inputmode="decimal" data-rent="${esc(id)}" value="${esc(st.rents[id] ?? '')}" data-money /></div>
            <div class="field"><label>${esc(t('move_in_wiz.terms.kozos'))}</label><input class="input" type="number" min="0" step="1" inputmode="decimal" data-kozos="${esc(id)}" value="${esc(st.kozos[id] ?? '')}" data-money /></div>
          </div>
          <div class="field-row" style="grid-template-columns:1fr 1fr">
            <div class="field"><label>${esc(t('move_in_wiz.terms.rent_vat'))}</label>${vatSelect(`data-rent-vat="${esc(id)}"`, st.rentVat[id])}</div>
            <div class="field"><label>${esc(t('move_in_wiz.terms.kozos_vat'))}</label>${vatSelect(`data-kozos-vat="${esc(id)}"`, st.kozosVat[id])}</div>
          </div>
        </div>`;
    }).join('');
    return `
      <div class="stack">
        <div class="sub-head">${esc(t('move_in_wiz.terms.term'))}</div>
        <div class="field-row" style="grid-template-columns:1fr 160px">
          <div class="field"><label>${esc(t('move_in_wiz.terms.start_date'))} <span class="req">*</span></label><input class="input" type="date" name="start_date" value="${esc(st.startDate)}" /></div>
          <div class="field"><label>${esc(t('move_in_wiz.terms.currency'))}</label>
            <select class="select" name="currency">${['HUF', 'EUR', 'USD'].map((c) => `<option ${st.currency === c ? 'selected' : ''}>${c}</option>`).join('')}</select>
          </div>
        </div>
        <div class="field"><label>${esc(t('move_in_wiz.terms.type'))}</label>
          <div class="seg" style="align-self:flex-start">${seg('open', t('move_in_wiz.terms.type_open'))}${seg('fixed', t('move_in_wiz.terms.type_fixed'))}</div>
        </div>
        ${st.termType === 'fixed' ? `<div class="field"><label>${esc(t('move_in_wiz.terms.end_date'))}</label><input class="input" type="date" name="end_date" value="${esc(st.endDate)}" /></div>` : ''}

        <div class="sub-head">${esc(t('move_in_wiz.terms.rent_section'))}</div>
        <span class="faint">${esc(t('move_in_wiz.terms.rent_hint'))}</span>
        ${rentRows}

        <div class="sub-head">${esc(t('move_in_wiz.terms.deposit_section'))}</div>
        <div class="field"><label>${esc(t('move_in_wiz.terms.deposit'))} <span class="faint">(${esc(t('common.optional'))})</span></label>
          <input class="input" type="number" min="0" step="1" inputmode="decimal" name="deposit" value="${esc(st.deposit)}" data-money />
          <span class="faint">${esc(t('move_in_wiz.terms.deposit_hint'))}</span>
        </div>

        <div class="sub-head">${esc(t('move_in_wiz.charge.section'))}</div>
        <span class="faint">${esc(t('move_in_wiz.charge.hint'))}</span>
        <div class="stack-2" data-charge-list>${this.state.charges.map((c, i) => this._chargeRow(c, i)).join('')}</div>
        <button type="button" class="btn btn-ghost btn-sm" data-charge-add style="align-self:flex-start">${esc(t('move_in_wiz.charge.add'))}</button>

        <div class="mtotal"><span class="muted">${esc(t('move_in_wiz.terms.monthly_total'))}</span><strong data-mtotal>${esc(this._monthlyTotalLabel())}</strong></div>
      </div>`;
  }

  _chargeRow(c, i) {
    return `
      <div class="charge-row" data-charge-row="${i}">
        <select class="select" data-charge-type>${CHARGE_TYPES.map((tp) => `<option value="${tp}" ${c.charge_type === tp ? 'selected' : ''}>${esc(t('move_in_wiz.charge.types.' + tp))}</option>`).join('')}</select>
        <input class="input" type="number" min="0" step="1" inputmode="decimal" data-charge-amount data-money value="${esc(c.amount ?? '')}" placeholder="${esc(t('move_in_wiz.charge.amount'))}" />
        ${vatSelect('data-charge-vat title="' + esc(t('move_in_wiz.charge.vat')) + '"', c.vat_rate)}
        <select class="select" data-charge-freq>
          <option value="monthly" ${c.frequency === 'monthly' ? 'selected' : ''}>${esc(t('move_in_wiz.charge.freq_monthly'))}</option>
          <option value="once" ${c.frequency === 'once' ? 'selected' : ''}>${esc(t('move_in_wiz.charge.freq_once'))}</option>
        </select>
        <button type="button" class="icon-btn" data-charge-rm="${i}" title="${esc(t('move_in_wiz.charge.remove'))}">${icon('trash', { size: 'sm' })}</button>
      </div>`;
  }

  _monthlyTotalLabel() {
    const st = this.state;
    let total = 0;
    for (const id of st.selectedUnitIds) {
      total += addVat(majorToMinor(st.rents[id] || 0) || 0, st.rentVat[id]);
      total += addVat(majorToMinor(st.kozos[id] || 0) || 0, st.kozosVat[id]);
    }
    for (const c of st.charges) {
      if (c.frequency === 'monthly') total += addVat(majorToMinor(c.amount || 0) || 0, c.vat_rate);
    }
    return fmtMoney(total, st.currency);
  }

  _wireTerms() {
    const root = this.el;
    root.querySelectorAll('[data-term]').forEach((b) => b.addEventListener('click', () => { this._capture(); this.setState({ termType: b.getAttribute('data-term') }); }));
    const recompute = () => {
      this._capture();
      const el = root.querySelector('[data-mtotal]');
      if (el) el.textContent = this._monthlyTotalLabel();
    };
    root.querySelectorAll('[data-money], [name="currency"], [data-charge-type], [data-charge-freq]').forEach((e) => e.addEventListener('input', recompute));
    root.querySelectorAll('[data-charge-type], [data-charge-freq], [data-charge-vat], [data-rent-vat], [data-kozos-vat]').forEach((e) => e.addEventListener('change', recompute));
    const add = root.querySelector('[data-charge-add]');
    if (add) add.addEventListener('click', () => { this._capture(); this.setState({ charges: [...this.state.charges, { charge_type: 'utilities', amount: '', frequency: 'monthly' }] }); });
    root.querySelectorAll('[data-charge-rm]').forEach((b) => b.addEventListener('click', () => {
      this._captureCharges();
      const arr = this.state.charges.slice(); arr.splice(Number(b.getAttribute('data-charge-rm')), 1);
      this.setState({ charges: arr });
    }));
  }

  // ── step 5: review ───────────────────────────────────────────────────
  _reviewHtml() {
    const st = this.state;
    const p = st.tenantMode === 'existing' ? (st.selectedParty || {}) : st.newTenant;
    const prop = (st.properties || []).find((x) => x.id === st.selectedPropertyId) || {};
    const unitLabels = st.selectedUnitIds.map((id) => ((st.units || []).find((u) => u.id === id) || {}).identifier || id);
    let rentTotal = 0; for (const id of st.selectedUnitIds) rentTotal += majorToMinor(st.rents[id] || 0) || 0;
    const card = (titleKey, goto, rows) => `
      <div class="card stack-2">
        <div class="row-between"><span class="sub-head" style="margin:0">${esc(t('move_in_wiz.review.' + titleKey))}</span><button type="button" class="editlink" data-goto="${goto}">${esc(t('move_in_wiz.review.edit'))}</button></div>
        ${rows}
      </div>`;
    const kv = (k, v) => `<div class="kv-row"><span class="faint">${esc(k)}</span><strong>${esc(v || '—')}</strong></div>`;
    const occRows = st.occupants.length
      ? st.occupants.map((o) => `<div class="kv-row"><strong>${esc(o.display_name)}</strong><span class="badge badge-accent">${esc(t('move_in_wiz.rel.' + o.relationship))}</span></div>`).join('')
      : `<span class="faint">${esc(t('move_in_wiz.occ.none'))}</span>`;
    return `
      <div class="stack">
        ${card('place', 0, kv(t('move_in_wiz.place.property'), prop.name) + kv(t('move_in_wiz.review.units'), unitLabels.join(', ')))}
        ${card('tenant', st.tenantMode === 'existing' ? 1 : 2,
          kv(t('move_in_wiz.data.name'), p.display_name)
          + kv(t('move_in_wiz.data.mother_name'), p.mother_name)
          + kv(t('move_in_wiz.data.birth_place'), [p.birth_place, p.birth_date].filter(Boolean).join(', '))
          + kv(t('move_in_wiz.data.email'), p.email))}
        ${card('occupants', 3, occRows)}
        ${card('terms', 4,
          kv(t('move_in_wiz.review.start'), st.startDate)
          + kv(t('move_in_wiz.review.type'), st.termType === 'fixed' ? (t('move_in_wiz.terms.type_fixed') + (st.endDate ? ' · ' + st.endDate : '')) : t('move_in_wiz.terms.type_open'))
          + kv(t('move_in_wiz.review.rent_total'), fmtMoney(rentTotal, st.currency))
          + kv(t('move_in_wiz.review.deposit'), st.deposit ? fmtMoney(majorToMinor(st.deposit), st.currency) : '—')
          + (st.charges.length ? kv(t('move_in_wiz.charge.section'), st.charges.filter((c) => c.amount !== '' && c.amount != null).map((c) => `${t('move_in_wiz.charge.types.' + (c.charge_type || 'other'))} ${fmtMoney(majorToMinor(c.amount), st.currency)}${c.frequency === 'monthly' ? '/hó' : ''}`).join(' · ')) : ''))}
        <div class="callout-soft row-between">
          <div class="stack-2"><strong>${esc(t('move_in_wiz.review.portal_title'))}</strong><span class="faint">${esc(t('move_in_wiz.review.portal_body'))}</span></div>
          <span class="badge badge-muted">${esc(t('move_in_wiz.review.portal_soon'))}</span>
        </div>
      </div>`;
  }

  // ── data loading ───────────────────────────────────────────────────────
  async _load() {
    try {
      const props = await api.get('/properties');
      const st = { loading: false, properties: props.data || [] };
      // One eligible property → skip the pick, go straight to its units.
      if (!this.state.selectedPropertyId) {
        const eligible = st.properties.filter((p) => Number(p.unit_count ?? 0) > 0);
        if (eligible.length === 1) st.selectedPropertyId = eligible[0].id;
      }
      // Opened from a tenant ("Move in" on the Contacts list): preselect the
      // party so the tenant step starts with it already chosen (still changeable).
      if (this._initialPartyId) {
        try {
          const party = await api.get(`/tenant-parties/${this._initialPartyId}`);
          if (party && party.id) { st.selectedParty = party; st.tenantMode = 'existing'; }
        } catch { /* unknown party → fall back to the normal tenant step */ }
        this._initialPartyId = '';
      }
      this.setState(st);
      if (this.state.selectedPropertyId) this._loadUnits(this.state.selectedPropertyId);
    } catch (err) {
      this.setState({ loading: false, error: err instanceof ApiError ? err.message : t('move_in_wiz.errors.load') });
    }
  }

  async _loadUnits(propertyId) {
    try {
      const res = await api.get(`/properties/${propertyId}/units`);
      const units = res.data || [];
      const st = { units };
      // Pre-select + auto-fill rent/közös from the unit the wizard was opened from.
      if (this._initialUnitId) {
        const u = units.find((x) => x.id === this._initialUnitId);
        const blocked = u && u.availability_status === 'occupied' && u.current_tenancy_open_ended;
        if (u && !blocked) st.selectedUnitIds = [u.id];
        this._initialUnitId = '';
      } else if (this.state.selectedUnitIds.length === 0 && units.length === 1) {
        // Single-unit property → preselect the only unit (still deselectable).
        const only = units[0];
        const blocked = only.availability_status === 'occupied' && only.current_tenancy_open_ended;
        if (!blocked) st.selectedUnitIds = [only.id];
      }
      // Seed rent/közös defaults + currency for every loaded unit.
      const rents = { ...this.state.rents }, kozos = { ...this.state.kozos };
      let currency = this.state.currency;
      for (const u of units) {
        if (rents[u.id] === undefined) rents[u.id] = minorToMajor(u.asking_rent);
        if (kozos[u.id] === undefined) kozos[u.id] = minorToMajor(u.kozos_koltseg);
        if (u.currency) currency = u.currency;
      }
      st.rents = rents; st.kozos = kozos; st.currency = currency;
      this.setState(st);
    } catch (err) {
      this.setState({ units: [], error: err instanceof ApiError ? err.message : t('move_in_wiz.errors.load') });
    }
  }

  // ── submit ─────────────────────────────────────────────────────────────
  async _save() {
    this._capture();
    const st = this.state;
    const units = st.selectedUnitIds.map((id) => {
      const rent = majorToMinor(st.rents[id] || 0);
      const k = st.kozos[id];
      return {
        unit_id: id,
        rent_amount: Number.isFinite(rent) ? rent : 0,
        rent_vat_rate: Number(st.rentVat[id] ?? 0) || 0,
        ...(k !== undefined && k !== '' ? {
          kozos_koltseg: majorToMinor(k),
          kozos_vat_rate: Number(st.kozosVat[id] ?? 0) || 0,
        } : {}),
      };
    });
    const occupants = st.occupants.map((o) => o.mode === 'existing'
      ? { tenant_party_id: o.tenant_party_id, relationship: o.relationship }
      : {
        relationship: o.relationship,
        party: {
          display_name: o.display_name,
          birth_name: o.birth_name || null,
          birth_place: o.birth_place || null,
          birth_date: o.birth_date || null,
          mother_name: o.mother_name || null,
        },
      });
    const depositMinor = st.deposit !== '' ? majorToMinor(st.deposit) : null;
    const charges = st.charges
      .filter((c) => c.amount !== '' && c.amount != null && Number.isFinite(majorToMinor(c.amount)))
      .map((c) => ({ charge_type: c.charge_type || 'other', amount: majorToMinor(c.amount), frequency: c.frequency || 'monthly', vat_rate: Number(c.vat_rate ?? 0) || 0 }));

    this.setState({ busy: true, formError: null });
    try {
      // New tenant → create the reusable party first, then reuse it.
      let partyId = st.selectedParty && st.tenantMode === 'existing' ? st.selectedParty.id : null;
      if (st.tenantMode === 'new') {
        const party = await api.post('/tenant-parties', { ...st.newTenant, entity_type: 'individual' });
        partyId = party.id;
      }
      const payload = {
        source: 'TRACK_ONLY',
        tenant_party_id: partyId,
        currency: st.currency || 'HUF',
        start_date: st.startDate,
        units,
        ...(st.termType === 'fixed' && st.endDate ? { end_date: st.endDate } : {}),
        ...(depositMinor !== null && Number.isFinite(depositMinor) ? { deposit_amount: depositMinor } : {}),
        ...(occupants.length ? { occupants } : {}),
        ...(charges.length ? { charges } : {}),
      };
      const tenancy = await api.post('/tenancies', payload);
      window.location.hash = '#/tenancies/' + tenancy.id;
    } catch (err) {
      this.setState({ busy: false, formError: err instanceof ApiError ? err.message : t('move_in_wiz.errors.create') });
    }
  }
}
